import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Container,
} from '@mui/material';
import {
  Check,
  Warning,
  ExitToApp,
} from '@mui/icons-material';
import { validateTerminationToken, signTermination } from '../../api/termination';

interface TerminationData {
  id: string;
  address: string;
  apartmentNumber: string;
  city: string;
  tenantFirstName: string;
  tenantLastName: string;
  moveOutDate: string;
  reason?: string;
  initiatedDate: string;
}

type ErrorCode = 'TOKEN_NOT_FOUND' | 'ALREADY_SIGNED' | 'LINK_EXPIRED' | 'INTERNAL_ERROR' | null;

const TerminationSigningPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [terminationData, setTerminationData] = useState<TerminationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<ErrorCode>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (token) {
      validateToken();
    }
  }, [token]);

  const validateToken = async () => {
    try {
      setLoading(true);
      setError(null);
      setErrorCode(null);

      if (!token) {
        setError('Token saknas');
        setErrorCode('TOKEN_NOT_FOUND');
        setLoading(false);
        return;
      }

      const result = await validateTerminationToken(token);

      if (!result.success) {
        setError(result.error || 'Ogiltig eller utgången länk');
        setErrorCode((result.errorCode as ErrorCode) || 'INTERNAL_ERROR');
        setLoading(false);
        return;
      }

      if (result.data) {
        setTerminationData(result.data);
      } else {
        setError('Kunde inte hämta uppsägningsdata');
        setErrorCode('INTERNAL_ERROR');
      }

      setLoading(false);
    } catch (error: any) {
      console.error('Error validating termination token:', error);
      setError('Ett fel uppstod vid validering av länken');
      setErrorCode('INTERNAL_ERROR');
      setLoading(false);
    }
  };

  const handleSign = async () => {
    if (!token || !accepted) return;

    try {
      setSubmitting(true);
      setError(null);

      const result = await signTermination(token);

      if (!result.success) {
        setError(result.error || 'Ett fel uppstod vid signering');
        setSubmitting(false);
        return;
      }

      setSuccess(true);
    } catch (error) {
      console.error('Error signing termination:', error);
      setError('Ett fel uppstod vid signering. Försök igen.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Redan signerad
  if (errorCode === 'ALREADY_SIGNED') {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Card elevation={3}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Box sx={{
              width: 80, height: 80, borderRadius: '50%',
              backgroundColor: 'success.light',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto', mb: 3
            }}>
              <Check sx={{ fontSize: 48, color: 'success.dark' }} />
            </Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Redan signerad
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Du har redan signerat uppsägningen. Du behöver inte göra något mer.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Om du har frågor, kontakta support@hyria.se
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Ogiltig länk
  if (error && !terminationData) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Card elevation={3}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Box sx={{
              width: 80, height: 80, borderRadius: '50%',
              backgroundColor: 'warning.light',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto', mb: 3
            }}>
              <Warning sx={{ fontSize: 48, color: 'warning.dark' }} />
            </Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Länken är ogiltig
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Om du tror att detta är ett fel, kontakta support@hyria.se
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Signering lyckades
  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Card elevation={3}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Box sx={{
              width: 80, height: 80, borderRadius: '50%',
              backgroundColor: 'success.light',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto', mb: 3
            }}>
              <Check sx={{ fontSize: 48, color: 'success.dark' }} />
            </Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Uppsägning signerad
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Du har signerat uppsägningen. Lägenhetsägaren och föreningen har informerats.
              Uthyrningen avslutas den {terminationData?.moveOutDate}.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Om du har frågor, kontakta support@hyria.se
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Signeringsformulär
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box sx={{
          width: 64, height: 64, borderRadius: '50%',
          background: '#F59E0B',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto', mb: 2
        }}>
          <ExitToApp sx={{ fontSize: 32, color: 'white' }} />
        </Box>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Uppsägning av hyresavtal
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Lägenhet {terminationData?.apartmentNumber} på {terminationData?.address}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          {/* Detaljer */}
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            Uppsägningsdetaljer
          </Typography>

          <Box sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Namn:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {terminationData?.tenantFirstName} {terminationData?.tenantLastName}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Adress:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {terminationData?.address}, {terminationData?.city}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Lägenhet:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {terminationData?.apartmentNumber}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Utflyttningsdatum:</Typography>
                <Typography variant="body2" fontWeight="bold" color="warning.main">
                  {terminationData?.moveOutDate}
                </Typography>
              </Box>
              {terminationData?.reason && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Anledning:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {terminationData.reason}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Info */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Genom att signera bekräftar du att du har tagit del av uppsägningen och
              accepterar att uthyrningen avslutas på det angivna utflyttningsdatumet.
            </Typography>
          </Alert>

          {/* Checkbox */}
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  disabled={submitting}
                />
              }
              label={
                <Typography variant="body2">
                  Jag har tagit del av uppsägningen och bekräftar att uthyrningen avslutas
                  den {terminationData?.moveOutDate}.
                </Typography>
              }
            />
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={handleSign}
              disabled={!accepted || submitting}
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Check />}
              size="large"
              sx={{
                background: '#F59E0B',
                '&:hover': { background: '#D97706' }
              }}
            >
              {submitting ? 'Signerar...' : 'Signera uppsägning'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Har du frågor? Kontakta support@hyria.se
        </Typography>
      </Box>
    </Container>
  );
};

export default TerminationSigningPage;
